<template>
  <div class="daily-expenses-card card h-100">
    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-bar-chart"></i>
      </div>
      <div class="header-content text-start">
        <h3 class="card-title">Daily Spending</h3>
        <p class="card-subtitle">Your spending rhythm this month</p>
      </div>
      <button @click="showModal = true" class="expand-btn d-none d-md-flex" title="Expand chart">
        <i class="fa-solid fa-expand"></i>
      </button>
    </div>

    <div class="card-body">
      <div class="chart-container">
        <canvas ref="chartCanvas"></canvas>
      </div>
      <div v-if="dailyExpensesData.length === 0" class="text-center text-muted py-4">
        <p>No spending data yet</p>
      </div>
    </div>

    <!-- Full Screen Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <div class="modal-title-section">
            <div class="modal-icon">
              <i class="bi bi-bar-chart"></i>
            </div>
            <div>
              <h2 class="modal-title">Daily Spending Chart</h2>
              <p class="modal-subtitle">Your spending rhythm this month - Detailed View</p>
            </div>
          </div>
          <button @click="closeModal" class="modal-close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="expanded-chart-container">
            <canvas ref="expandedChartCanvas" width="800" height="400"></canvas>
          </div>
          
          <div class="chart-stats">
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-calendar-month me-1"></i>
                Total Spent This Month
              </div>
              <div class="stat-value expense-value">
                ${{ totalExpensesThisMonth.toFixed(2) }}
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-chart-line me-1"></i>
                Typical Daily Spending
              </div>
              <div class="stat-value">
                ${{ averageDailySpending.toFixed(2) }}
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                <i class="fa-solid fa-exclamation-triangle me-1"></i>
                Days Over Target
              </div>
              <div class="stat-value" :class="{ 'expense-value': daysOverBudget > 0 }">
                {{ daysOverBudget }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js'
import { shallowRef } from 'vue'
import { useDarkMode } from '../composables/useDarkMode'
import { useBudgetData } from '../composables/useBudgetData'

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
)

export default {
  name: "DailyExpensesChart",
  setup() {
    const { isDarkMode, currentTheme } = useDarkMode()
    const { currentDate, isViewingCurrentMonth, actualCurrentDate, budgetStartDate, isPartialMonth } = useBudgetData()
    return {
      isDarkMode,
      currentTheme,
      currentDate,
      isViewingCurrentMonth,
      actualCurrentDate,
      budgetStartDate,
      isPartialMonth
    }
  },
  props: {
    transactions: {
      type: Array,
      default: () => []
    },
    dailyBudget: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      chart: shallowRef(null),
      expandedChart: shallowRef(null),
      showModal: false,
      isUpdating: false,
      cachedChartData: null,
      updateTimeout: null
    }
  },
  computed: {
    dailyExpensesData() {
      if (!this.transactions || this.transactions.length === 0) {
        return []
      }

      // Use currentDate from composable (viewed month) instead of hardcoded "now"
      const viewedMonth = this.currentDate
      const viewedYear = viewedMonth.getFullYear()
      const viewedMonthIndex = viewedMonth.getMonth()
      
      // For current month, only show up to today
      // For past months, show all days of the month
      const lastDayToShow = this.isViewingCurrentMonth 
        ? new Date(this.actualCurrentDate.getFullYear(), this.actualCurrentDate.getMonth(), this.actualCurrentDate.getDate())
        : new Date(viewedYear, viewedMonthIndex + 1, 0) // Last day of viewed month
      
      // Determine first day to show
      // For partial month, start from budgetStartDate
      // Otherwise, start from first day of month
      let firstDay
      if (this.isPartialMonth && this.budgetStartDate) {
        firstDay = new Date(this.budgetStartDate)
      } else {
        firstDay = new Date(viewedYear, viewedMonthIndex, 1)
      }
      
      const lastDayOfMonth = new Date(viewedYear, viewedMonthIndex + 1, 0)

      // Create a map to store daily expenses
      const dailyExpenses = new Map()

      // Initialize days from firstDay to lastDayOfMonth (inclusive)
      const lastDay = lastDayOfMonth.getDate()
      for (let day = firstDay.getDate(); day <= lastDay; day++) {
        const currentDate = new Date(viewedYear, viewedMonthIndex, day)
        const dateKey = currentDate.toDateString()
        // For current month: 0 for past/current days, null for future days
        // For past months: 0 for all days
        if (this.isViewingCurrentMonth) {
          dailyExpenses.set(dateKey, currentDate <= lastDayToShow ? 0 : null)
        } else {
          dailyExpenses.set(dateKey, 0)
        }
      }

      // Calculate daily expenses from transactions
      this.transactions.forEach(transaction => {
        if (transaction.type === 'debit') {
          // Handle different date formats more robustly
          let transactionDate
          if (typeof transaction.date === 'string') {
            transactionDate = new Date(transaction.date)
          } else {
            transactionDate = new Date(transaction.date)
          }
          
          // Normalize to local date (remove time component)
          const normalizedDate = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate())
          const dateKey = normalizedDate.toDateString()

          // Include transactions from viewed month up to lastDayToShow
          if (normalizedDate >= firstDay && normalizedDate <= lastDayToShow) {
            const currentAmount = dailyExpenses.get(dateKey) || 0
            dailyExpenses.set(dateKey, currentAmount + transaction.amount)
          }
        }
      })

      // Convert to array format for Chart.js
      const result = []
      dailyExpenses.forEach((amount, dateKey) => {
        const date = new Date(dateKey)
        result.push({
          date: date,
          day: date.getDate(),
          amount: amount, // null for future days (current month only), number otherwise
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        })
      })

      return result.sort((a, b) => a.date - b.date)
    },

    chartData() {
      // Force recalculation when theme changes by accessing currentTheme
      const primaryColor = this.currentTheme && this.getPrimaryColor();
      const dangerColor = '#dc3545';
      const warningColor = '#ffc107';
      
      return {
        labels: this.dailyExpensesData.map(item => item.label),
        datasets: [
          {
            label: 'Daily Spending',
            data: this.dailyExpensesData.map(item => item.amount),
            borderColor: primaryColor,
            backgroundColor: this.hexToRgba(primaryColor, 0.1),
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: this.dailyExpensesData.map(item =>
              item.amount === null ? 'transparent' : 
              item.amount > this.dailyBudget ? dangerColor : primaryColor
            ),
            pointBorderColor: this.dailyExpensesData.map(item =>
              item.amount === null ? 'transparent' : 
              item.amount > this.dailyBudget ? dangerColor : primaryColor
            ),
            pointRadius: this.dailyExpensesData.map(item => item.amount === null ? 0 : 4),
            pointHoverRadius: this.dailyExpensesData.map(item => item.amount === null ? 0 : 6),
            spanGaps: false // Don't connect line across null values
          },
          {
            label: 'Daily Target',
            data: new Array(this.dailyExpensesData.length).fill(this.dailyBudget),
            borderColor: warningColor,
            backgroundColor: this.hexToRgba(warningColor, 0.1),
            borderWidth: 2,
            pointRadius: 0,
            borderDash: [5, 5],
            fill: false
          }
        ]
      }
    },

    chartOptions() {
      const textColor = this.getCSSVariableValue('--light-text');
      const textMutedColor = this.getCSSVariableValue('--light-text-muted');
      const borderColor = this.getCSSVariableValue('--light-border');

      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: textColor,
              usePointStyle: true,
              padding: 20,
              font: {
                size: 14
              }
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: this.getPrimaryColor(),
            borderWidth: 1,
            callbacks: {
              label: function (context) {
                if (context.dataset.label === 'Daily Target') {
                  return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`
                }
                return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Amount ($)',
              color: textMutedColor,
              font: {
                size: 12
              }
            },
            grid: {
              color: this.getCSSVariableValue('--light-border')
            },
            ticks: {
              color: textMutedColor,
              font: {
                size: 10
              },
              callback: function (value) {
                return '$' + value.toFixed(0)
              }
            }
          },
          x: {
            title: {
              display: true,
              text: 'Day of Month',
              color: textMutedColor,
              font: {
                size: 12
              }
            },
            grid: {
              color: this.getCSSVariableValue('--light-border')
            },
            ticks: {
              color: textMutedColor,
              font: {
                size: 10
              },
              maxRotation: 45,
              minRotation: 0
            }
          }
        }
      }
    },

    totalExpensesThisMonth() {
      return this.dailyExpensesData
        .filter(item => item.amount !== null)
        .reduce((sum, item) => sum + item.amount, 0);
    },

    averageDailySpending() {
      const validDays = this.dailyExpensesData.filter(item => item.amount !== null);
      if (validDays.length === 0) return 0;
      return this.totalExpensesThisMonth / validDays.length;
    },

    daysOverBudget() {
      return this.dailyExpensesData
        .filter(item => item.amount !== null && item.amount > this.dailyBudget)
        .length;
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.chartCanvas) {
        this.createChart()
      }
    })
  },
  watch: {
    transactions: {
      handler() {
        this.debouncedUpdate()
      },
      deep: true
    },
    dailyBudget() {
      this.debouncedUpdate()
    },
    currentDate() {
      // Update chart when month changes
      this.debouncedUpdate()
    },
    currentTheme() {
      // Update charts when theme changes
      this.$nextTick(() => {
        this.updateChart();
        if (this.expandedChart) {
          this.updateExpandedChart();
        }
      });
    },
    showModal(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.createExpandedChart();
        });
      } else if (this.expandedChart) {
        this.expandedChart.destroy();
        this.expandedChart = shallowRef(null);
      }
    }
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this.expandedChart) {
      this.expandedChart.destroy()
    }
  },
  methods: {
    getCSSVariableValue(variableName) {
      // Read from body element since data-theme attribute is applied there
      const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement;
      const value = getComputedStyle(element).getPropertyValue(variableName).trim();
      return value;
    },

    getPrimaryColor() {
      return this.getCSSVariableValue('--btn-primary-color');
    },

    hexToRgba(hex, alpha) {
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    createExpandedChart() {
      if (!this.$refs.expandedChartCanvas) return;
      const ctx = this.$refs.expandedChartCanvas.getContext('2d');
      this.expandedChart = shallowRef(this.createChartInstance(ctx, true));
    },

    createChartInstance(ctx, isExpanded = false) {
      const expensesData = this.computeExpensesData();
      if (!expensesData.length) return null;

      const primaryColor = this.getPrimaryColor();
      const dangerColor = '#dc3545';
      const warningColor = '#ffc107';

      const chartData = {
        labels: expensesData.map(item => item.label),
        datasets: [
          {
            label: 'Daily Spending',
            data: expensesData.map(item => item.amount),
            borderColor: primaryColor,
            backgroundColor: this.hexToRgba(primaryColor, 0.1),
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: expensesData.map(item =>
              item.amount === null ? 'transparent' : 
              item.amount > this.dailyBudget ? dangerColor : primaryColor
            ),
            pointBorderColor: expensesData.map(item =>
              item.amount === null ? 'transparent' : 
              item.amount > this.dailyBudget ? dangerColor : primaryColor
            ),
            pointRadius: expensesData.map(item => item.amount === null ? 0 : (isExpanded ? 4 : 4)),
            pointHoverRadius: expensesData.map(item => item.amount === null ? 0 : (isExpanded ? 6 : 6)),
            spanGaps: false
          },
          {
            label: 'Daily Target',
            data: new Array(expensesData.length).fill(this.dailyBudget),
            borderColor: warningColor,
            backgroundColor: this.hexToRgba(warningColor, 0.1),
            borderWidth: 2,
            pointRadius: 0,
            borderDash: [5, 5],
            fill: false
          }
        ]
      };

      return new Chart(ctx, {
        type: 'line',
        data: chartData,
        options: this.getChartOptions(isExpanded)
      });
    },

    getChartOptions(isExpanded = false) {
      const textColor = this.getCSSVariableValue('--light-text');
      const textMutedColor = this.getCSSVariableValue('--light-text-muted');
      const borderColor = this.getCSSVariableValue('--light-border');

      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: isExpanded,
            position: 'top',
            labels: {
              color: textColor,
              usePointStyle: true,
              padding: 20,
              font: {
                size: 14
              }
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: this.getPrimaryColor(),
            borderWidth: 1,
            titleFont: {
              size: isExpanded ? 14 : 12
            },
            bodyFont: {
              size: isExpanded ? 13 : 11
            },
            callbacks: {
              label: function (context) {
                if (context.dataset.label === 'Daily Target') {
                  return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`
                }
                return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Amount ($)',
              color: textMutedColor,
              font: {
                size: isExpanded ? 14 : 12
              }
            },
            grid: {
              color: this.getCSSVariableValue('--light-border')
            },
            ticks: {
              color: textMutedColor,
              font: {
                size: isExpanded ? 12 : 10
              },
              callback: function (value) {
                return '$' + value.toFixed(0)
              }
            }
          },
          x: {
            title: {
              display: true,
              text: 'Day of Month',
              color: textMutedColor,
              font: {
                size: isExpanded ? 14 : 12
              }
            },
            grid: {
              color: this.getCSSVariableValue('--light-border')
            },
            ticks: {
              color: textMutedColor,
              font: {
                size: isExpanded ? 12 : 10
              },
              maxRotation: 45,
              minRotation: 0
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        elements: {
          point: {
            hoverBackgroundColor: this.getPrimaryColor(),
            radius: isExpanded ? 4 : 0,
            hoverRadius: isExpanded ? 6 : 0
          }
        }
      };
    },

    updateChart() {
      if (this.chart) {
        // Destroy and recreate chart to update theme colors
        this.chart.destroy();
        this.createChart();
      }
    },

    updateChartData() {
      if (this.chart) {
        // Just update data without recreating chart
        const expensesData = this.computeExpensesData();
        if (!expensesData.length) return;

        const primaryColor = this.getPrimaryColor();
        const dangerColor = '#dc3545';

        this.chart.data.labels = expensesData.map(item => item.label);
        this.chart.data.datasets[0].data = expensesData.map(item => item.amount);
        this.chart.data.datasets[0].pointBackgroundColor = expensesData.map(item =>
          item.amount === null ? 'transparent' : 
          item.amount > this.dailyBudget ? dangerColor : primaryColor
        );
        this.chart.data.datasets[0].pointBorderColor = expensesData.map(item =>
          item.amount === null ? 'transparent' : 
          item.amount > this.dailyBudget ? dangerColor : primaryColor
        );
        this.chart.data.datasets[1].data = new Array(expensesData.length).fill(this.dailyBudget);
        this.chart.update('none');
      }
    },

    updateExpandedChart() {
      if (this.expandedChart) {
        // Destroy and recreate expanded chart to update theme colors
        this.expandedChart.destroy();
        this.createExpandedChart();
      }
    },

    updateExpandedChartData() {
      if (this.expandedChart) {
        // Just update data without recreating chart
        const expensesData = this.computeExpensesData();
        if (!expensesData.length) return;

        const primaryColor = this.getPrimaryColor();
        const dangerColor = '#dc3545';

        this.expandedChart.data.labels = expensesData.map(item => item.label);
        this.expandedChart.data.datasets[0].data = expensesData.map(item => item.amount);
        this.expandedChart.data.datasets[0].pointBackgroundColor = expensesData.map(item =>
          item.amount === null ? 'transparent' : 
          item.amount > this.dailyBudget ? dangerColor : primaryColor
        );
        this.expandedChart.data.datasets[0].pointBorderColor = expensesData.map(item =>
          item.amount === null ? 'transparent' : 
          item.amount > this.dailyBudget ? dangerColor : primaryColor
        );
        this.expandedChart.data.datasets[1].data = new Array(expensesData.length).fill(this.dailyBudget);
        this.expandedChart.update('none');
      }
    },

    closeModal() {
      this.showModal = false;
    },

    createChart() {
      if (this.isUpdating) return

      // Clean up existing chart
      if (this.chart) {
        this.chart.destroy()
        this.chart = shallowRef(null)
      }

      try {
        const ctx = this.$refs.chartCanvas?.getContext('2d')
        if (!ctx) {
          console.warn('Chart canvas context not available')
          return
        }

        this.chart = shallowRef(this.createChartInstance(ctx, false));
      } catch (error) {
        console.error('Chart creation error:', error)
        this.chart = shallowRef(null)
      }
    },

    updateChart() {
      if (this.chart) {
        // Destroy and recreate chart to update theme colors
        this.chart.destroy();
        this.createChart();
      }
    },

    debouncedUpdate() {
      if (!this.isUpdating) {
        this.updateChartData()
        if (this.expandedChart) {
          this.updateExpandedChartData()
        }
        this.$nextTick(() => {
          this.isUpdating = false
        })
      }
    },

    computeExpensesData() {
      if (!this.transactions || this.transactions.length === 0) {
        return []
      }

      // Use currentDate from composable (viewed month) instead of hardcoded "now"
      const viewedMonth = this.currentDate
      const viewedYear = viewedMonth.getFullYear()
      const viewedMonthIndex = viewedMonth.getMonth()
      
      // For current month, only show up to today
      // For past months, show all days of the month
      const lastDayToShow = this.isViewingCurrentMonth 
        ? new Date(this.actualCurrentDate.getFullYear(), this.actualCurrentDate.getMonth(), this.actualCurrentDate.getDate())
        : new Date(viewedYear, viewedMonthIndex + 1, 0) // Last day of viewed month
      
      // Determine first day to show
      // For partial month, start from budgetStartDate
      // Otherwise, start from first day of month
      let firstDay
      if (this.isPartialMonth && this.budgetStartDate) {
        firstDay = new Date(this.budgetStartDate)
      } else {
        firstDay = new Date(viewedYear, viewedMonthIndex, 1)
      }
      
      const lastDayOfMonth = new Date(viewedYear, viewedMonthIndex + 1, 0)

      // Create a map to store daily expenses
      const dailyExpenses = new Map()

      // Initialize days from firstDay to lastDayOfMonth (inclusive)
      const lastDay = lastDayOfMonth.getDate()
      for (let day = firstDay.getDate(); day <= lastDay; day++) {
        const currentDate = new Date(viewedYear, viewedMonthIndex, day)
        const dateKey = currentDate.toDateString()
        // For current month: 0 for past/current days, null for future days
        // For past months: 0 for all days
        if (this.isViewingCurrentMonth) {
          dailyExpenses.set(dateKey, currentDate <= lastDayToShow ? 0 : null)
        } else {
          dailyExpenses.set(dateKey, 0)
        }
      }

      // Calculate daily expenses from transactions
      this.transactions.forEach(transaction => {
        if (transaction.type === 'debit') {
          // Handle different date formats more robustly
          let transactionDate
          if (typeof transaction.date === 'string') {
            transactionDate = new Date(transaction.date)
          } else {
            transactionDate = new Date(transaction.date)
          }
          
          // Normalize to local date (remove time component)
          const normalizedDate = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate())
          const dateKey = normalizedDate.toDateString()

          // Include transactions from viewed month up to lastDayToShow
          if (normalizedDate >= firstDay && normalizedDate <= lastDayToShow) {
            const currentAmount = dailyExpenses.get(dateKey) || 0
            dailyExpenses.set(dateKey, currentAmount + transaction.amount)
          }
        }
      })

      // Convert to array format for Chart.js
      const result = []
      dailyExpenses.forEach((amount, dateKey) => {
        const date = new Date(dateKey)
        result.push({
          date: date,
          day: date.getDate(),
          amount: amount, // null for future days (current month only), number otherwise
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        })
      })

      return result.sort((a, b) => a.date - b.date)
    }
  }
}
</script>

<style scoped>
.daily-expenses-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  flex: 1;
  text-align: left;
}

/* On mobile, when expand button is hidden, align content to start */
@media (max-width: 768px) {
  .card-header {
    justify-content: flex-start;
  }
}

.expand-btn {
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.1rem;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.expand-btn:hover {
  color: var(--light-text);
  background: var(--light-border);
}

.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background: var(--light-card);
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--light-border);
  background: var(--nested-container-bg);
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.modal-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #198754, #20c997);
  color: white;
  font-size: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text);
  margin: 0;
}

.modal-subtitle {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0.25rem 0 0 0;
}

.modal-close-btn {
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.25rem;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.modal-close-btn:hover {
  color: var(--light-text);
  background: var(--light-border);
}

.modal-body {
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
}

.expanded-chart-container {
  position: relative;
  height: 500px;
  width: 100%;
  margin-bottom: 2rem;
}

.chart-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.stat-item {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1.5rem;
  border: 2px dashed var(--light-border);
  transition: all 0.2s ease;
}

.stat-item:hover {
  border-color: var(--light-text);
}

.stat-label {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--light-success);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.stat-value.expense-value {
  color: var(--light-danger);
}

/* Dark Mode Support */
:global([data-theme]) .modal-overlay {
  background: rgba(0, 0, 0, 0.9);
}

:global([data-theme]) .modal-content {
  background: var(--light-card);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-header {
    padding: 1rem 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .expanded-chart-container {
    height: 350px;
  }

  .chart-stats {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .stat-item {
    padding: 1.25rem;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}
</style>